import { useEffect, useMemo } from "react";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { Group } from "../../members/models/Group";
import { Member } from "../../members/models/Member";
import { Session } from "../models/Session";
import { selectSessionSeats } from "../seats-slice";
import { fetchSessionSeats } from "../slice-extensions/seats-slice-extension";
import { buildAttendanceRoster } from "../utils/buildAttendanceRoster";
import { countSingleDateMembers } from "../utils/countSingleDateMembers";
import { isoDayFromToday } from "../utils/fullestOccupancy";

/** The longest window the Session's seat read serves. */
const SEAT_WINDOW_DAYS = 62;

/**
 * Members booked on single dates of a saved Session: an upcoming Confirmed seat in the
 * next 62 days, not on the series roster, each member once. Zero for an unsaved Session.
 */
export const useSingleDateMemberCount = (
  session: Session | null | undefined,
  members: Member[],
  groups: Group[],
): number => {
  const dispatch = useAppDispatch();
  const sessionId = session?.id ?? "";
  const seats = useAppSelector(selectSessionSeats(sessionId));

  useEffect(() => {
    if (!sessionId) return;
    dispatch(fetchSessionSeats({ bookingId: sessionId, from: isoDayFromToday(0), to: isoDayFromToday(SEAT_WINDOW_DAYS) }));
  }, [dispatch, sessionId]);

  return useMemo(() => {
    if (!session?.id) return 0;
    const roster = buildAttendanceRoster(session, members, groups).memberIds;
    return countSingleDateMembers(seats, roster, isoDayFromToday(0));
  }, [session, members, groups, seats]);
};
